import Phaser from 'phaser';
import { Port } from './Port';
import { GRID_SIZE, gridToWorld } from '../grid';
import type { Cell, Direction, MoleculeMix } from '../types';
import { coneOffsetFromPorthole, portholeOffsetFromCell } from '../util/cone';

export type PortShape = 'cone' | 'castellated';

export interface OrganPortDef {
  id: string;
  kind: 'spout' | 'receptacle' | 'bidirectional';
  facing: Direction;
  // Offset from the organ's top-left grid cell to the port's connection cell.
  // The connection cell is OUTSIDE the organ body (one cell away in the facing direction).
  cellOffset: { dCol: number; dRow: number };
  carries: MoleculeMix;
  /**
   * Receptacles only. Additional source mixes that count as CORRECT in addition
   * to `carries`. See `PortConfig.acceptsAlso` for full semantics.
   */
  acceptsAlso?: MoleculeMix[];
  /**
   * Visual style of the port. 'cone' (default) is a wall/normal port that pipes
   * connect to via a draggable pipe. 'castellated' is the interlocking square-wave
   * piece used for organ-to-organ direct links — when two castellated ports share
   * a cell, their teeth interlock visually as one joint.
   */
  shape?: PortShape;
  /**
   * Output spouts only. List of input-port IDs (globally unique) that must each
   * currently be receiving their declared `carries` exactly for this spout to
   * emit. Evaluated every frame by `Connector.update`. If absent or empty, the
   * spout is unconditional (acts like a wall spout — always emits when fluids
   * are running).
   *
   * Dep-satisfied predicate: the input port's incoming pipe is `flowing` AND
   * the pipe's source emits a mix that classifies as CORRECT against the input
   * port's carries+acceptsAlso (per `classifyPipe`). ACCEPTABLE-tier flow does
   * NOT satisfy a dep.
   *
   * Mutually exclusive with `transform`. If both declared, `transform` wins.
   */
  dependsOn?: string[];
  /**
   * Receptacles only. When `true`, the port accepts ANY incoming mix as
   * CORRECT (no burst, no UNACCEPTABLE tier). Used by organs whose output
   * spout's `transform` function composes its emission from arbitrary inputs
   * (e.g. intestines side-in passes whatever it receives through, plus food
   * if the top is also feeding food).
   *
   * NOTE: the port's `carries` field is still required (used cosmetically
   * for the porthole icon and as documentation).
   */
  acceptsAny?: boolean;
  /**
   * Output spouts only. **v5.1 escape hatch** for composition logic that the
   * declarative `dependsOn` form can't express (e.g. intestines side-out
   * passes through side-in's mix plus food if top has food). When present,
   * this REPLACES `dependsOn` + `carries` for emission logic.
   *
   * Receives a `Map<portId, MoleculeMix>` of currently-delivering inputs
   * (only ports whose incoming pipe is delivered + classifying CORRECT are
   * included). Returns the mix to emit right now, or `null` for "no
   * emission" (pipe stays gray / cone-tip spurt stops).
   *
   * Called every frame by `Connector.update`. Should be a pure function of
   * the inputs map — no scene access. When the returned mix changes mid-flow,
   * the active `FlowFx` gets `setCarries(newMix)` so emission updates live.
   */
  transform?: (inputs: Map<string, MoleculeMix>) => MoleculeMix | null;
  /**
   * Receptacles only. See `PortConfig.validIf` — gating predicate for organ
   * prerequisites. When false, any incoming pipe classifies as UNACCEPTABLE
   * and bursts on arrival. Used for lungs' left input (requires diaphragm).
   */
  validIf?: (inputs: Map<string, MoleculeMix>) => boolean;
  /**
   * Cosmetic only — rotates the cone sprite 180° in place. See
   * `PortConfig.coneFlipped`. Used on the lungs' top trachea endpoint so the
   * cone visually plugs INTO the organ rather than out from it.
   */
  coneFlipped?: boolean;
}

/**
 * `art` is a string key that maps to procedurally-generated textures
 * `organ-{art}-idle` (required) and `organ-{art}-active` (optional, for
 * organs that pulse when activated). Each art key gets its own module in
 * `src/scenes/textures/{art}.ts` — keeping this as plain `string` so adding a
 * new organ doesn't force a union-type edit.
 */
export type OrganArt = string;

export interface OrganDef {
  id: string; // unique within a level — e.g. "brain"
  art: OrganArt; // texture key root: organ-{art}-idle (+ organ-{art}-active)
  cols: number; // organ footprint in grid cells
  rows: number;
  ports: OrganPortDef[];
  /**
   * Optional. List of THIS ORGAN's output port IDs. When every listed output
   * is currently emitting (its dependsOn rule satisfied), `organ.activate()`
   * runs (pulse + texture swap). When any goes back to non-emitting,
   * `organ.deactivate()`. Used by the brain in level 3 (v5.2 work).
   */
  activatesWhen?: string[];
}

/**
 * An organ instance placed in the workspace. Owns its sprite and its Port instances.
 * The port instances are also added to the scene's global ports list so PipeBuilder
 * and Connector can treat them like any other port.
 */
export class Organ {
  readonly def: OrganDef;
  cell: Cell; // top-left grid cell (mutable — updated when student moves the organ)
  readonly sprite: Phaser.GameObjects.Image;
  ports: Port[];

  private scene: Phaser.Scene;
  private activated = false;
  private pulseTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, def: OrganDef, cell: Cell) {
    this.scene = scene;
    this.def = def;
    this.cell = cell;

    const center = this.cellToCenterWorld(cell);
    this.sprite = scene.add.image(center.x, center.y, this.textureKey(false));
    this.sprite.setDepth(10); // above pipes, below port cones (ports register at depth 20)
    this.sprite.setInteractive({ draggable: true, useHandCursor: true });

    this.ports = this.buildPortsAt(cell);
  }

  /** World position of the organ-sprite's center for a given top-left cell. */
  cellToCenterWorld(cell: Cell): { x: number; y: number } {
    const topLeft = gridToWorld(cell);
    return {
      x: topLeft.x - GRID_SIZE / 2 + (this.def.cols * GRID_SIZE) / 2,
      y: topLeft.y - GRID_SIZE / 2 + (this.def.rows * GRID_SIZE) / 2,
    };
  }

  /** Snap the sprite to the world center of `cell` (without rebuilding ports). */
  snapSpriteTo(cell: Cell): void {
    const center = this.cellToCenterWorld(cell);
    this.sprite.setPosition(center.x, center.y);
  }

  /** Tear down the existing port containers and rebuild them at the given cell. */
  rebuildPortsAt(cell: Cell): void {
    for (const p of this.ports) p.container.destroy();
    this.cell = cell;
    this.ports = this.buildPortsAt(cell);
  }

  private buildPortsAt(cell: Cell): Port[] {
    // Organ ports omit the porthole (hideBase) — the organ sprite is the body.
    return this.def.ports.map(
      (pd) =>
        new Port(this.scene, {
          id: pd.id,
          kind: pd.kind,
          facing: pd.facing,
          connectionCell: {
            col: cell.col + pd.cellOffset.dCol,
            row: cell.row + pd.cellOffset.dRow,
          },
          carries: pd.carries,
          acceptsAlso: pd.acceptsAlso,
          dependsOn: pd.dependsOn,
          acceptsAny: pd.acceptsAny,
          transform: pd.transform,
          validIf: pd.validIf,
          coneFlipped: pd.coneFlipped,
          hideBase: true,
          shape: pd.shape ?? 'cone',
        }),
    );
  }

  /** Switch to the "active" organ texture and play a brief pulse. */
  activate(): void {
    if (this.activated) return;
    this.activated = true;
    const activeKey = this.textureKey(true);
    if (this.scene.textures.exists(activeKey)) {
      this.sprite.setTexture(activeKey);
    }
    this.pulseTween = this.scene.tweens.add({
      targets: this.sprite,
      scale: { from: 1, to: 1.08 },
      duration: 350,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Return to idle (used when stopFluids / clearAll runs). */
  deactivate(): void {
    if (!this.activated) return;
    this.activated = false;
    this.sprite.setTexture(this.textureKey(false));
    this.pulseTween?.stop();
    this.pulseTween = undefined;
    this.sprite.setScale(1);
  }

  /**
   * Build a non-interactive preview composite (organ sprite + cone protrusions)
   * for use as a pool icon thumbnail or drag ghost. Cones are positioned exactly
   * where they would be once the organ is placed, so the preview matches the final.
   */
  static buildPreview(
    scene: Phaser.Scene,
    def: OrganDef,
    opts: { scale?: number; alpha?: number } = {},
  ): Phaser.GameObjects.Container {
    const container = scene.add.container(0, 0);
    const sprite = scene.add.image(0, 0, `organ-${def.art}-idle`);
    container.add(sprite);

    for (const pd of def.ports) {
      if (pd.shape === 'castellated') {
        // Castellated piece sits exactly on the connection cell (relative to organ center).
        const cellRelX = GRID_SIZE * (pd.cellOffset.dCol + 0.5 - def.cols / 2);
        const cellRelY = GRID_SIZE * (pd.cellOffset.dRow + 0.5 - def.rows / 2);
        const tex = pd.kind === 'spout' ? 'port-castellated-spout' : 'port-castellated-receptacle';
        const piece = scene.add.image(cellRelX, cellRelY, tex);
        const facingAngle: Record<Direction, number> =
          pd.kind === 'spout' ? { S: 0, W: 90, N: 180, E: 270 } : { N: 0, E: 90, S: 180, W: 270 };
        piece.setAngle(facingAngle[pd.facing]);
        container.add(piece);
      } else {
        const placement = computeOrganPortConeOffset(pd, def);
        const cone = scene.add.image(placement.x, placement.y, 'port-cone');
        cone.setOrigin(0, 0.5);
        cone.setAngle(placement.angle);
        container.add(cone);
      }
    }

    if (opts.scale !== undefined) container.setScale(opts.scale);
    if (opts.alpha !== undefined) container.setAlpha(opts.alpha);
    return container;
  }

  destroy(): void {
    this.pulseTween?.stop();
    this.sprite.destroy();
    for (const p of this.ports) p.container.destroy();
  }

  private textureKey(active: boolean): string {
    return `organ-${this.def.art}-${active ? 'active' : 'idle'}`;
  }
}

/**
 * Compute a port's cone position+angle in coordinates relative to the organ's
 * center. Composes two pure helpers from `src/util/cone.ts`:
 *   1. Port's connection-cell center relative to organ center.
 *   2. Porthole offset from the connection cell (it hides inside the organ body).
 *   3. Cone offset from the porthole — shared with `Port.ts`.
 */
function computeOrganPortConeOffset(
  pd: OrganPortDef,
  def: OrganDef,
): { x: number; y: number; angle: number } {
  const cellRelX = GRID_SIZE * (pd.cellOffset.dCol + 0.5 - def.cols / 2);
  const cellRelY = GRID_SIZE * (pd.cellOffset.dRow + 0.5 - def.rows / 2);
  const ph = portholeOffsetFromCell(pd.facing);
  // Bidirectional ports use the spout cone visual (protruding outward).
  // `coneFlipped` swaps to the opposite kind's visual — see PortConfig.coneFlipped.
  const baseKind = pd.kind === 'receptacle' ? 'receptacle' : 'spout';
  const visualKind = pd.coneFlipped
    ? baseKind === 'spout'
      ? 'receptacle'
      : 'spout'
    : baseKind;
  const cone = coneOffsetFromPorthole(visualKind, pd.facing);
  return {
    x: cellRelX + ph.dx + cone.x,
    y: cellRelY + ph.dy + cone.y,
    angle: cone.angle,
  };
}
